import { useQuery } from '@tanstack/react-query';

import { api } from 'api/v1/api';

export type ReviewSort = 'newest' | 'rating_desc' | 'rating_asc';

export type TutorReviewItem = {
  id: number;
  tutor_profile_id: number;
  student_name: string;
  rating: number;
  comment?: string | null;
  date_create: string;
  verified: boolean;
};

export type TutorReviewsPage = {
  items: TutorReviewItem[];
  total: number;
  limit: number;
  offset: number;
};

export type TutorReviewStats = {
  total_reviews: number;
  average_rating: number;
  rating_distribution: Record<string, number>;
};

type ReviewsQuery = {
  tutorUserId: number;
  sort?: ReviewSort;
  limit?: number;
  offset?: number;
  publicAccess?: boolean;
};

export function useTutorReviews({
  tutorUserId,
  sort = 'newest',
  limit = 5,
  offset = 0,
  publicAccess = false,
}: ReviewsQuery) {
  const prefix = publicAccess ? 'public' : 'auth';
  return useQuery({
    queryKey: ['tutor-reviews', prefix, tutorUserId, sort, limit, offset],
    queryFn: async () => {
      const path = publicAccess
        ? `/tutors/${tutorUserId}/reviews/public`
        : `/tutors/${tutorUserId}/reviews`;
      const response = await api.get<TutorReviewsPage>(path, {
        params: { sort, limit, offset },
        skipGlobalErrorHandler: publicAccess,
      } as never);
      return response.data;
    },
    enabled: Number.isFinite(tutorUserId) && tutorUserId > 0,
    staleTime: 30_000,
  });
}

export function useTutorReviewStats(tutorUserId?: number, publicAccess = false) {
  const prefix = publicAccess ? 'public' : 'auth';
  return useQuery({
    queryKey: ['tutor-review-stats', prefix, tutorUserId],
    queryFn: async () => {
      const path = publicAccess
        ? `/tutors/${tutorUserId}/reviews/stats/public`
        : `/tutors/${tutorUserId}/reviews/stats`;
      const response = await api.get<TutorReviewStats>(path, {
        skipGlobalErrorHandler: publicAccess,
      } as never);
      return response.data;
    },
    enabled: Number.isFinite(tutorUserId) && (tutorUserId ?? 0) > 0,
    staleTime: 60_000,
  });
}
